import { onlyNumbers } from '@/helpers/common';
import { decrypt } from '@/helpers/crypt/crypt';
import { unmask, updateMask as updateMaskCash } from '@/helpers/masks/cash';
import validators from '@/helpers/validators';
import { useDialog } from '@/hooks/useDialog';
import useForm from '@/hooks/useForm';
import CardClient from '@/model/CardClient';
import CardClientCrypt from '@/model/CardClientCrypt';
import CardDb from '@/model/CardDb';
import { CardValidationResponse } from '@/model/CardValidationResponse';
import CartPayment from '@/model/CartPayment';
import Client from '@/model/Client';
import PaymentType from '@/model/PaymentType';
import { ValidateCardcheckout } from '@/model/ValidateCardcheckout';
import { setLoading } from '@/redux/loading/loadingSlice';
import { api, AxiosError } from '@/services/api';
import React, { JSX, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CardValidationResponseStatus from '@/model/CardValidationResponseStatus';
import { ValidatedCreditCardUI } from './ui';
import { FormInputCard, ShouldShowModalProps, ShowModalPayment } from '../types';

interface CustomError {
  message: string;
  details: string[];
}

export const ValidatedCreditCardScreen: React.FC = (): JSX.Element => {
  const { id } = useParams();

  const [cardPendingValidation, setCardPendingValidation] = useState<boolean>(false);
  const [cardValidationResponses, setCardValidationResponses] = useState<CardValidationResponse[]>(
    [],
  );
  const [cardSelected, setCardSelected] = useState<CardDb>();
  const [shouldShowModal, setShouldShowModal] = useState<ShowModalPayment>(
    ShowModalPayment.VALIDATE_CREDIT_CARD,
  );

  const [messageError, setMessageError] = useState<string[]>([]);
  const [titleError, setTitleError] = useState<string>('');

  const { visible, onSetVisible, onToggle, title, onChangeTitle } = useDialog();

  const handleOnShowModal = ({ value, newTitle }: ShouldShowModalProps): void => {
    setShouldShowModal(value);
    onChangeTitle(newTitle);
    onSetVisible(true);
  };
  const dispatch = useDispatch();
  const history = useNavigate();

  const handleError = (messages: string[], error: CardValidationResponseStatus): void => {
    let errorStatus = '';

    switch (error) {
      case CardValidationResponseStatus.DISAPPROVED:
        errorStatus = 'Cartão Recusado';
        break;
      case CardValidationResponseStatus.ERROR:
        errorStatus = 'Erro ao validar o cartão';
        break;
      case CardValidationResponseStatus.NOT_BELONG:
        errorStatus = 'Cartão Não Pertence ao Cliente';
        break;
      default:
        errorStatus = 'Erro ao validar o cartão';
        break;
    }
    setMessageError(messages);
    setTitleError(errorStatus);
    handleOnShowModal({
      value: ShowModalPayment.VALIDATE_ERROR,
      newTitle: '',
    });
  };

  const getValidateCreditCard = async (): Promise<void> => {
    try {
      dispatch(setLoading(true));
      const { data } = await api.get<Client>('client/my-account');
      const response = await api.get<CardClientCrypt>(`client/my-account/card/${id}`);
      const cardDB = JSON.parse(decrypt(response.data.data)) as CardDb;
      let errorMessages: string[] | undefined;
      const newCartPayments: CartPayment[] = [];
      const newValidatingCreditCard: CardClient[] = [];
      if (!cardDB.validated) {
        setCardPendingValidation(true);
        handleOnShowModal({
          value: ShowModalPayment.VALIDATE_CREDIT_CARD,
          newTitle: '',
        });
        const cards = data.cards as CardClient[];
        // eslint-disable-next-line no-plusplus
        for (let c = 0; c < cards.length; c++) {
          if (cards[c].id === cardDB.id) {
            const { REACT_APP_PAGSEGURO_TOKEN } = process.env;
            const expirationDate = cardDB.expirationDate.split('/');
            const cardPagSeguro = PagSeguro.encryptCard({
              publicKey: REACT_APP_PAGSEGURO_TOKEN,
              holder: cardDB.cardholderName,
              number: onlyNumbers(cardDB.number),
              expMonth: expirationDate[0],
              expYear: expirationDate[1],
              securityCode: cardDB.cvv,
            });
            if (cardPagSeguro.errors && cardPagSeguro.errors.length > 0) {
              errorMessages = [];
              // eslint-disable-next-line no-loop-func
              cardPagSeguro.errors.forEach(err => {
                errorMessages?.push(err.message);
              });
              break;
            } else {
              const encrypted = cardPagSeguro.encryptedCard;
              const cartPayment: CartPayment = {
                paymentType: PaymentType.CREDIT_CARD,
                installments: 1,
                clientCardId: cardDB.id,
                encrypted,
                paymentOriginalValue: 0,
                paymentValue: 0,
                paymentFee: 0,
              };
              newCartPayments.push(cartPayment);
              newValidatingCreditCard.push(cards[c]);
            }
            break;
          }
        }
        setCardSelected(cardDB);

        try {
          const validatedResponse = await api.post<CardValidationResponse[]>(
            'client/my-account/card/validate',
            newCartPayments,
          );
          if (validatedResponse.data) {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < validatedResponse.data.length; i++) {
              if (
                validatedResponse.data[i].status === CardValidationResponseStatus.NOT_BELONG ||
                validatedResponse.data[i].status === CardValidationResponseStatus.ERROR ||
                validatedResponse.data[i].status === CardValidationResponseStatus.DISAPPROVED
              ) {
                if (
                  validatedResponse.data[i].status === CardValidationResponseStatus.ERROR ||
                  validatedResponse.data[i].status === CardValidationResponseStatus.DISAPPROVED
                ) {
                  // eslint-disable-next-line no-await-in-loop
                  await api.delete<CardClient>(
                    `client/my-account/card/${newCartPayments[0].clientCardId}`,
                  );
                }
                handleError(
                  validatedResponse.data[i].messages || ['Houve um erro ao validar o cartão'],
                  validatedResponse.data[i].status,
                );
                break;
              }
            }
            setCardValidationResponses(validatedResponse.data);
          } else {
            handleError(['Houve um erro ao validar o cartão'], CardValidationResponseStatus.ERROR);
          }
        } catch (error) {
          const axiosError = error as AxiosError<CustomError, CustomError>;
          // eslint-disable-next-line no-console
          console.error(axiosError.response?.data.message);
          toast.error('Erro ao fazer cobrança temporaria!');
          dispatch(setLoading(false));
        }
      } else {
        setCardPendingValidation(true);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const axiosError = error as AxiosError<CustomError, CustomError>;
        // eslint-disable-next-line no-console
        console.error(axiosError.response?.data.message);
        toast.error('Erro ao buscar os cartões');
      } else {
        toast.error('Erro ao buscar os cartões');
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  const { formData, formErrors, setErrors, onChangeFormInput, resetForm } = useForm({
    initialData: {
      value: '0,00',
    },
    validators: {
      value: [validators.required],
    },
    formatters: {
      value: updateMaskCash,
    },
  });

  const handleOnCheckValidationCreditCardData = async (): Promise<void> => {
    try {
      dispatch(setLoading(true));
      const value = Number(unmask(formData[FormInputCard.value]));
      const payload: ValidateCardcheckout = {
        cardId: cardSelected?.id as string,
        value,
      };
      const response = await api.post<CardValidationResponse[]>(
        'client/my-account/card/validate/checkout',
        payload,
      );
      resetForm();
      setCardValidationResponses(response.data);
      setCardPendingValidation(false);
      toast.success('Cartão validado com sucesso!');
    } catch (error) {
      const messagesValidationError: string[] = [];
      if (error instanceof AxiosError) {
        const axiosError = error as AxiosError<CustomError, CustomError>;
        if (axiosError.response && axiosError.response.data) {
          if (axiosError.response.data.details && axiosError.response.data.details.length > 0) {
            axiosError.response.data.details.forEach(data => {
              messagesValidationError.push(data);
            });
          } else if (axiosError.response.data.message) {
            messagesValidationError.push(axiosError.response.data.message);
            setErrors({ value: [axiosError.response.data.message] });
            toast.error(axiosError.response.data.message);
          } else {
            messagesValidationError.push('Houve um erro ao processar o seu cartão');
            setErrors({ value: ['Houve um erro ao processar o seu cartão'] });
            toast.error('Houve um erro ao processar o seu cartão');
          }
        } else {
          messagesValidationError.push('Houve um erro ao processar o seu cartão');
          setErrors({ value: ['Houve um erro ao processar o seu cartão'] });
          toast.error('Houve um erro ao processar o seu cartão');
        }
      } else {
        messagesValidationError.push('Houve um erro ao processar o seu cartão');
        setErrors({ value: ['Houve um erro ao processar o seu cartão'] });
        toast.error('Houve um erro ao processar o seu cartão');
      }
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleGoCheckout = (): void => {
    history('/checkout');
  };

  const handleGoAddCard = (): void => {
    history('/create-card');
  };

  useEffect(() => {
    getValidateCreditCard();
  }, [id]);

  return (
    <ValidatedCreditCardUI
      cardPendingValidation={cardPendingValidation}
      cardValidationResponses={cardValidationResponses}
      cardSelected={cardSelected}
      formData={formData}
      onChangeFormInput={onChangeFormInput}
      formErrors={formErrors}
      onCancel={onToggle}
      onConfirm={handleOnCheckValidationCreditCardData}
      onCheckout={handleGoCheckout}
      visible={visible}
      title={title}
      onToggle={onToggle}
      shouldShowModal={shouldShowModal}
      onClose={onToggle}
      goAddCard={handleGoAddCard}
      titleError={titleError}
      messageError={messageError}
    />
  );
};
